import React, { useCallback, useRef } from 'react';
import clsx from 'clsx';
import { BLOCKED_DATES_LIST } from 'consts';
import { useStore } from 'effector-react';
import { useOnClickOutside } from 'hooks';
import { changeFocusedPickerDay, picker_state$ } from 'models/cart';
import DatePickerDisclosureItem from 'pages/Product/MainBlock/DatesPanel/DatePickerDisclosureItem';
import { useCalendarSettings } from 'pages/Product/useCalendarSettings';
import { getFormattedHireDates } from 'utils/datesСalculation';

import { Button, DatePickerCalendar, T8y } from 'ui';

import style from './calendarModal.module.scss';

const CalendarModal = ({
  product,
  suggestions = [],
  dates,
  setIsCalendarModalOpen,
  isDatesHighlighted,
  isReservationPage,
  handleDatesChange,
  gaEvent,
}) => {
  const state = useStore(picker_state$);

  const {
    selectedValues,
    onDatesChanges,
    hiringDaysCount,
    minBookingDays,
    minBookingDate,
    maxBookingDate,
  } = useCalendarSettings(product, dates);

  const wrapperRef = useRef(null);
  useOnClickOutside(wrapperRef, () => setIsCalendarModalOpen(false));

  const { startDate, endDate } = getFormattedHireDates(
    'en-gb',
    selectedValues.startDate,
    selectedValues.endDate,
  );

  const isSaveBtnDisabled =
    !selectedValues.startDate || !selectedValues.endDate;

  const handleSaveClick = useCallback(() => {
    if (isSaveBtnDisabled) {
      return;
    }

    handleDatesChange(selectedValues);

    setIsCalendarModalOpen(false);
  }, [
    handleDatesChange,
    isSaveBtnDisabled,
    selectedValues,
    setIsCalendarModalOpen,
  ]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={clsx(
        style.calendarModalWrapper,
        style.bigModalEngland,
        isReservationPage && style.reservationPage,
      )}
      ref={wrapperRef}
    >
      <div className={style.calendarModalHeader}>
        <div className={style.calendarModalTextQuantity}>
          <T8y variant="t3" color="newGray">
            Duration:
          </T8y>
          <T8y
            variant="t1"
            color={hiringDaysCount ? '#000000' : 'newGray'}
            bold
          >
            {hiringDaysCount}
            &nbsp;{hiringDaysCount === 1 ? 'day' : 'days'}
          </T8y>
        </div>
        <div className={style.calendarModalDateItems}>
          <DatePickerDisclosureItem
            date={!!selectedValues.startDate && startDate}
            titleSlug="hireStart"
            isDatesHighlighted={isDatesHighlighted}
            isInCalendar
          />
          <DatePickerDisclosureItem
            date={!!selectedValues.endDate && endDate}
            titleSlug="hireEnd"
            isDatesHighlighted={isDatesHighlighted}
            isInCalendar
          />
        </div>
      </div>
      {!!suggestions.length && (
        <div className={style.suggestionsWrapper}>
          {suggestions.map((el, index) => (
            <div
              key={index}
              onClick={() => {
                onDatesChanges(el, true);
              }}
              className={style.suggestionWrapper}
            >
              {el.formattedStr}
            </div>
          ))}
        </div>
      )}
      <div className={style.modalCalendarWrapper}>
        <DatePickerCalendar
          calendarState={state}
          start={selectedValues.startDate}
          end={selectedValues.endDate}
          handleDateChange={onDatesChanges}
          minBookingDate={minBookingDate}
          minBookingDays={minBookingDays}
          maxBookingDate={maxBookingDate}
          edge={state?.focusedInput === 'startDate' ? 'start' : 'end'}
          blockedDatesList={BLOCKED_DATES_LIST}
        />
      </div>
      <div className={style.calendarModalFooter}>
        <T8y
          variant="t2"
          color="newGray"
          className={style.clearText}
          onClick={() => {
            onDatesChanges({ startDate: null, endDate: null });
            changeFocusedPickerDay('start');
          }}
        >
          Clear dates
        </T8y>
        <Button
          onClick={handleSaveClick}
          className={clsx(style.saveBtn, isSaveBtnDisabled && style.disabled)}
          data-testid="save-dates-btn"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default CalendarModal;
